import { getNamedaySnapshot, watchCalendar } from './salex-calendar.js';

export function selectGiftProducts(products, filters = {}, limit = 8) {
  const recipientAge = filters.age === 'all' || filters.age == null ? null : Number(filters.age);
  const budget = filters.budget === 'all' || filters.budget == null ? null : Number(filters.budget) * 100;
  if (recipientAge !== null && (!Number.isInteger(recipientAge) || recipientAge < 0 || recipientAge > 99)) return [];
  if (budget !== null && (!Number.isFinite(budget) || budget <= 0)) return [];
  return products.map((product, index) => ({ product, index }))
    .filter(({ product }) => {
      if (product.available !== true || !Number.isInteger(product.price) || product.price <= 0) return false;
      if (product.christmasOnly === true && filters.season !== 'christmas') return false;
      if (budget !== null && product.price > budget) return false;
      if (recipientAge !== null && (!Number.isInteger(product.minAge) || product.minAge > recipientAge || product.minAge < 0)) return false;
      return true;
    })
    .sort((a, b) => recipientAge === null ? a.index - b.index : b.product.minAge - a.product.minAge || a.index - b.index)
    .slice(0, Math.max(0, Math.floor(limit)))
    .map(({ product }) => product);
}

export function normalizeGiftProducts(value) {
  if (!Array.isArray(value)) return [];
  return value.slice(0, 50).filter(product => product && typeof product.title === 'string' && product.title.trim()
    && typeof product.url === 'string' && /^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?products\/[^\s<>]+$/i.test(product.url)
    && product.available === true && Number.isInteger(product.price) && product.price > 0)
    .map(product => ({
      title: product.title,
      url: product.url,
      image: typeof product.image === 'string' && /^(?:https:)?\/\//i.test(product.image) ? (product.image.startsWith('//') ? `https:${product.image}` : product.image) : null,
      price: product.price,
      priceLabel: typeof product.priceLabel === 'string' ? product.priceLabel : '',
      minAge: Number.isInteger(product.minAge) && product.minAge >= 0 && product.minAge <= 99 ? product.minAge : null,
      christmasOnly: product.christmasOnly === true,
      available: true
    }));
}

export function formatGiftPrice(product, config = {}) {
  try {
    return new Intl.NumberFormat(config.locale || 'sk', { style: 'currency', currency: config.currency || 'EUR' }).format(product.price / 100);
  } catch (error) {
    return product.priceLabel || String(product.price / 100);
  }
}

function createElement(tag, className, text) {
  const element = document.createElement(tag);
  if (className) element.className = className;
  if (text != null) element.textContent = text;
  return element;
}

function startSeasonalInterface() {
  const config = window.SaleXSeasonalConfig || { mode: 'auto', texts: {} };
  const text = key => config.texts?.[key] || '';
  const controllers = new Map();
  const modes = new Set(['halloween', 'christmas', 'winter', 'evergreen']);

  function applyCalendar(snapshot) {
    const previousSeason = document.documentElement.dataset.sxSeason;
    const season = modes.has(config.mode) ? config.mode : snapshot.season;
    document.documentElement.dataset.sxSeason = season;
    for (const when of ['today', 'tomorrow']) {
      const day = snapshot[when];
      const holidayKey = `holiday_${day.key.slice(-5).replace('-', '_')}`;
      const label = day.names.length ? day.names.join(', ') : text(holidayKey) || text('holiday_other');
      document.querySelectorAll(`[data-sx-names="${when}"]`).forEach(node => { if (label) node.textContent = label; });
      document.querySelectorAll(`[data-sx-date="${when}"]`).forEach(node => { node.textContent = day.dateLabel; node.setAttribute('datetime', day.key); });
    }
    if (previousSeason !== season) {
      for (const [dialog, controller] of controllers) if (dialog.open) controller.refresh();
    }
  }

  function productCard(product) {
    const card = createElement('a', 'sx-gift-card');
    card.href = product.url;
    if (product.image) {
      const imageWrap = createElement('div', 'sx-gift-card__image');
      const image = createElement('img');
      image.src = product.image;
      image.alt = product.title;
      image.width = 480;
      image.height = 480;
      image.loading = 'lazy';
      imageWrap.append(image);
      card.append(imageWrap);
    }
    const copy = createElement('div', 'sx-gift-card__copy');
    copy.append(createElement('h3', '', product.title));
    if (product.minAge !== null) copy.append(createElement('span', 'sx-gift-card__age', text('age_min').replace('__age__', String(product.minAge))));
    copy.append(createElement('strong', 'sx-gift-card__price', formatGiftPrice(product, config)));
    copy.append(createElement('span', 'sx-gift-card__tax', text('tax_included')));
    const cta = createElement('span', 'sx-gift-card__cta');
    cta.append(createElement('span', '', text('view_product')));
    const arrow = createElement('span', '', '↗');
    arrow.setAttribute('aria-hidden', 'true');
    cta.append(arrow);
    copy.append(cta);
    card.append(copy);
    return card;
  }

  function bindGiftDialog(dialog) {
    if (controllers.has(dialog)) return;
    const dataNode = document.querySelector('[data-sx-gift-data]');
    let products = [];
    try { products = normalizeGiftProducts(JSON.parse(dataNode?.textContent || '[]')); } catch (error) { products = []; }
    const openers = [...document.querySelectorAll(`[data-sx-gift-open][aria-controls="${dialog.id}"]`)];
    if (!products.length || typeof dialog.showModal !== 'function') {
      for (const opener of openers) {
        opener.removeAttribute('aria-haspopup');
        opener.removeAttribute('aria-controls');
      }
      return;
    }
    const abort = new AbortController();
    const options = { signal: abort.signal };
    const form = dialog.querySelector('[data-sx-gift-filters]');
    const ageInput = dialog.querySelector('[data-sx-gift-age]');
    const budgetInput = dialog.querySelector('[data-sx-gift-budget]');
    const grid = dialog.querySelector('[data-sx-gift-products]');
    const empty = dialog.querySelector('[data-sx-gift-empty]');
    const status = dialog.querySelector('[data-sx-gift-status]');
    let focusReturn = null;

    function renderGifts() {
      const visible = selectGiftProducts(products, { age: ageInput.value, budget: budgetInput.value, season: document.documentElement.dataset.sxSeason });
      const fragment = document.createDocumentFragment();
      for (const product of visible) fragment.append(productCard(product));
      grid.replaceChildren(fragment);
      empty.hidden = visible.length > 0;
      status.textContent = visible.length ? text('results_count').replace('__count__', String(visible.length)) : text('no_results');
    }

    for (const opener of openers) {
      opener.addEventListener('click', event => {
        event.preventDefault();
        focusReturn = opener;
        renderGifts();
        if (!dialog.open) dialog.showModal();
        ageInput.focus({ preventScroll: true });
      }, options);
    }
    dialog.querySelector('[data-sx-gift-close]').addEventListener('click', () => dialog.close(), options);
    dialog.addEventListener('close', () => { if (focusReturn?.isConnected) focusReturn.focus({ preventScroll: true }); }, options);
    dialog.addEventListener('click', event => {
      if (event.target !== dialog) return;
      const rect = dialog.getBoundingClientRect();
      if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) dialog.close();
    }, options);
    form.addEventListener('submit', event => event.preventDefault(), options);
    form.addEventListener('change', renderGifts, options);
    controllers.set(dialog, {
      refresh: renderGifts,
      destroy() {
        if (dialog.open) dialog.close();
        abort.abort();
        controllers.delete(dialog);
      }
    });
  }

  function initialize() {
    for (const [dialog, controller] of controllers) if (!dialog.isConnected) controller.destroy();
    document.querySelectorAll('[data-sx-gift-dialog]').forEach(bindGiftDialog);
    applyCalendar(getNamedaySnapshot());
  }

  initialize();
  watchCalendar(applyCalendar);
  document.addEventListener('shopify:section:load', initialize);
  document.addEventListener('shopify:section:unload', event => {
    for (const [dialog, controller] of controllers) if (event.target.contains(dialog)) controller.destroy();
  });
}

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', startSeasonalInterface, { once: true });
  else startSeasonalInterface();
}
